import { theme } from '@agentisai/design-system';
import { useSearchParams } from 'react-router';
import { useWorkstreams } from '../queries/useWorkstreams';
import { RailNewButton } from './RailNewButton';
import { WorkstreamCard } from './WorkstreamCard';

/** URL search-param key for the currently-selected workstream (R3 — URL as truth). */
export const WS_PARAM = 'ws';
/** URL search-param keys for the Files View (S11): `?files=` is the open tab set
 * (comma-separated opaque ids), `?file=` is the active tab. Both are cleared on a
 * workstream switch — they belong to one workstream's ledger. */
export const FILES_PARAM = 'files';
export const FILE_PARAM = 'file';

/**
 * Workstream rail panel body (S-C3). Slots into the `WorkspaceShell` and
 * renders the caller's workstreams as a list of `WorkstreamCard` bubbles
 * sorted newest first (B2 already returns them in that order; we don't
 * re-sort client-side). Active selection is URL state via `useSearchParams`
 * (`?ws=<id>`, frontend-architecture R3) — survives reload, deep-linkable,
 * back-button works.
 *
 * Loading + error states are minimal placeholders for now (Drop 5 only):
 *   - loading → quiet skeleton text
 *   - error   → quiet "couldn't load" line (the QueryCache.onError sink
 *                already forwards to `reportError` for the diagnostic
 *                trail, so this UI is purely user-facing)
 *
 * Empty result → "create your first" affordance that opens the existing
 * CreateWorkstreamPanel via the `onNew` callback (consumer drives the modal
 * state — same pattern as the EmptyState CTA in HomePage today).
 */
export interface WorkstreamsRailBodyProps {
  /** Click handler for the `+ New` subheader action AND the empty-state CTA. */
  readonly onNew: () => void;
}

export function WorkstreamsRailBody({ onNew }: WorkstreamsRailBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get(WS_PARAM);
  const query = useWorkstreams();

  function select(id: string) {
    // Replace, not push — selecting a card shouldn't grow the history stack
    // (a back-button press should leave the workstreams view, not unselect).
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(WS_PARAM, id);
        // Drop any open file tabs — they belong to the workstream we're leaving, so
        // they wouldn't resolve in the new one (else the View pane would fetch
        // cross-workstream ids and only recover on a 404).
        next.delete(FILES_PARAM);
        next.delete(FILE_PARAM);
        return next;
      },
      { replace: true },
    );
  }

  if (query.isLoading) {
    return (
      <div
        style={{
          padding: theme.space[4],
          color: theme.color.neutral.faint,
          fontSize: theme.typography.fontSize.bodySm,
        }}
      >
        Loading workstreams…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        style={{
          padding: theme.space[4],
          color: theme.color.neutral.faint,
          fontSize: theme.typography.fontSize.bodySm,
        }}
      >
        Couldn't load workstreams. We'll retry automatically.
      </div>
    );
  }

  const items = query.data?.data ?? [];

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: theme.space[4],
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space[3],
        }}
      >
        <p
          style={{
            margin: 0,
            color: theme.color.neutral.faint,
            fontSize: theme.typography.fontSize.bodySm,
            lineHeight: theme.typography.lineHeight.body,
          }}
        >
          No workstreams yet.
        </p>
        <button
          type="button"
          onClick={onNew}
          style={{
            alignSelf: 'flex-start',
            background: theme.color.neutral.charcoal,
            color: theme.color.neutral.white,
            border: 0,
            padding: `${theme.space[1]} ${theme.space[3]}`,
            borderRadius: theme.radii.sm,
            fontFamily: theme.typography.fontFamily.sans,
            fontSize: theme.typography.fontSize.bodySm,
            fontWeight: theme.typography.fontWeight.semibold,
            cursor: 'pointer',
          }}
        >
          + Create your first workstream
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: `0 ${theme.space[4]}` }}>
      {items.map((item) => (
        <WorkstreamCard
          key={item.workstreamId}
          name={item.title}
          description={item.description ?? ''}
          active={activeId === item.workstreamId}
          onClick={() => select(item.workstreamId)}
        />
      ))}
    </div>
  );
}
WorkstreamsRailBody.displayName = 'WorkstreamsRailBody';

/**
 * The `+ New` subheader action button — extracted so the rail header can host
 * it while the body owns the loading/empty/list states. Same style + tokens as
 * the DS `RailPanel` story's `NewActionButton` (story fixture; not exported).
 */
export interface NewWorkstreamActionProps {
  readonly onClick: () => void;
}

export function NewWorkstreamAction({ onClick }: NewWorkstreamActionProps) {
  return <RailNewButton onClick={onClick}>+ New</RailNewButton>;
}
NewWorkstreamAction.displayName = 'NewWorkstreamAction';
